import React from "react";

import { isChunkLoadError, isReloadOnCooldown, markReload } from "../utils";

type Props = {
  children: React.ReactNode;
  loadingFallback: React.ReactNode;
  errorFallback: React.ReactNode;
};

type State = {
  error: Error | null;
  isGivingUp: boolean;
};

// Catches a route chunk that never loads. A stale-deploy chunk is fixed by one
// reload; anything else falls through to the error fallback.
class ChunkErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, isGivingUp: false };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && !isReloadOnCooldown()) {
      markReload();
      window.location.reload();
      return;
    }

    console.error(error);
    this.setState({ isGivingUp: true });
  }

  render() {
    const { error, isGivingUp } = this.state;
    const { children, loadingFallback, errorFallback } = this.props;

    if (!error) {
      return children;
    }

    // A reload is already on its way — keep the loader up instead of flashing an error.
    return isGivingUp ? errorFallback : loadingFallback;
  }
}

export default ChunkErrorBoundary;
